import { DOCUMENT, Injectable, afterNextRender, inject } from '@angular/core';

import { Service } from '@rtms/client';
import { NotificationParams } from '@rtms/client/lib/protocol';
import { NEVER, Observable, ReplaySubject, filter, first, map, switchMap } from 'rxjs';

import { DslService } from '../dsl/dsl.service';
import { UserEvent, UserLoginEvent } from '../user/user-events';
import { UserService } from '../user/user.service';
import { vanillaAppExport } from '../utils/vanilla-app-export';
import { RtmsMessage } from './rtms-message';
import { RtmsServiceFactory } from './rtms-service.factory';
import { RtmsConfig } from './rtms.client-config';

/**
 * @whatItDoes Allows subscribing to messages from Real-Time Messaging System implemented by the platform.
 *
 * @howToUse
 * ```
 * let sub = this.rtmsService.messages
 *     .filter(m => m.type === 'PLAYERINBOX_UPDATE')
 *     .subscribe(m => {
 *         this.messageCount = m.payload.newMsgCount;
 *     });
 * ```
 *
 * @description
 *
 * Delegates to `@rtms/client`.
 * Automatically reconnects on the user login.
 * Automatically reconnects on focusing browser tab if logged out.
 *
 * @stable
 */
@Injectable({
    providedIn: 'root',
})
export class RtmsService {
    private readonly userService = inject(UserService);
    private readonly config = inject(RtmsConfig);
    private readonly dslService = inject(DslService);
    readonly rtmsServiceFactory = inject(RtmsServiceFactory);

    private readonly _doc = inject(DOCUMENT);

    private readonly platformRtmsService$ = new ReplaySubject<Service>(1);

    constructor() {
        const rtmsServiceFactory = this.rtmsServiceFactory;
        if (!this.config.isEnabled) {
            return;
        }
        afterNextRender(() => {
            const rtmsService = rtmsServiceFactory.create();
            this.platformRtmsService$.next(rtmsService);

            this.userService.events
                .pipe(
                    filter((e: UserEvent) => e instanceof UserLoginEvent),
                    first(),
                )
                .subscribe(() => {
                    rtmsService.connect();
                });

            // Helper to mock RTMS event from the console: `vanillaApp.diagnostics.sendToSocket()`
            vanillaAppExport('diagnostics', 'sendToSocket', (event: NotificationParams) => (rtmsService.messages as any).next(event));
        });
    }

    /**
     * Gets the messages.
     * For each message, evaluates if the event is disabled according to dynacon condition and do not propagate its value.
     */
    get messages(): Observable<RtmsMessage> {
        if (this.config.isEnabled) {
            if (this.userService.isAuthenticated || !this.config.establishConnectionOnlyInLoginState) {
                return this.getMessages();
            }

            return this.userService.events.pipe(
                filter((e: UserEvent) => e instanceof UserLoginEvent),
                first(),
                switchMap(() => this.getMessages()),
            );
        }

        return NEVER;
    }

    private getMessages(): Observable<RtmsMessage> {
        return this.platformRtmsService$.pipe(
            switchMap((service) => service.messages),
            filter(
                (notification: NotificationParams) =>
                    this._doc.visibilityState === 'visible' ||
                    this.config.backgroundEvents?.some((e) => e.toLocaleLowerCase() === notification.type?.toLocaleLowerCase()),
            ),
            switchMap((notification: NotificationParams) =>
                this.dslService.evaluateExpression<boolean>(this.config.disabledEvents[notification.type?.toLowerCase()] ?? 'false').pipe(
                    first(),
                    filter((disabled: boolean) => !disabled),
                    map(() => notification),
                ),
            ),
        );
    }
}
